import { useApi } from '../composables/useApi'

const api = useApi()

export const employeeService = {
  // Get all employees with employment details
  getAll: async () => {
    try {
      const response = await api.get('/employees')
      return {
        success: true,
        data: response.data || []
      }
    } catch (error) {
      // Don't log permission errors (403) - these are expected for users without permissions
      if (error.response?.status === 403) {
        return {
          success: false,
          message: 'Permission denied',
          data: []
        }
      }
      console.error('Error fetching employees:', error)
      return {
        success: false,
        message: error.message || 'Failed to fetch employees',
        data: []
      }
    }
  },

  // Get single employee by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/employees/${id}`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('Error fetching employee:', error)
      return {
        success: false,
        message: error.message || 'Failed to fetch employee'
      }
    }
  },

  // Create new employee
  create: async (employeeData) => {
    try {
      const response = await api.post('/employees', employeeData)
      return {
        success: true,
        data: response.data,
        message: 'Employee created successfully'
      }
    } catch (error) {
      console.error('Error creating employee:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create employee',
        errors: error.response?.data?.errors || {}
      }
    }
  },

  // Update employee
  update: async (id, employeeData) => {
    try {
      const response = await api.put(`/employees/${id}`, employeeData)
      return {
        success: true,
        data: response.data,
        message: 'Employee updated successfully'
      }
    } catch (error) {
      console.error('Error updating employee:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update employee',
        errors: error.response?.data?.errors || {}
      }
    }
  },

  // Delete employee
  delete: async (id) => {
    try {
      await api.delete(`/employees/${id}`)
      return {
        success: true,
        message: 'Employee deleted successfully'
      }
    } catch (error) {
      console.error('Error deleting employee:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete employee'
      }
    }
  },

  // Get employee statistics
  getStats: async () => {
    try {
      const response = await api.get('/employees/stats/overview')
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('Error fetching employee stats:', error)
      return {
        success: false,
        message: error.message || 'Failed to fetch employee statistics'
      }
    }
  },

  // Get available users for linking to employees
  getAllUsers: async () => {
    try {
      const response = await api.get('/employees/available-users')
      return {
        success: true,
        data: response.data || []
      }
    } catch (error) {
      console.error('Error fetching available users:', error)
      return {
        success: false,
        message: error.message || 'Failed to fetch available users',
        data: []
      }
    }
  },

  // Get all users (including linked ones) for reference
  getAllSystemUsers: async () => {
    try {
      const response = await api.get('/users')
      return {
        success: true,
        data: response.data || []
      }
    } catch (error) {
      console.error('Error fetching all users:', error)
      return {
        success: false,
        message: error.message || 'Failed to fetch users',
        data: []
      }
    }
  },

  // Get assignable users for order creation (requires only orders.create permission)
  getAssignableUsers: async () => {
    try {
      const response = await api.get('/users/assignable')
      return {
        success: true,
        data: response.data || []
      }
    } catch (error) {
      console.error('Error fetching assignable users:', error)
      return {
        success: false,
        message: error.message || 'Failed to fetch assignable users',
        data: []
      }
    }
  },

  // Check name consistency between employee and user account
  checkNameConsistency: (employee) => {
    if (!employee.user_account) return null
    
    const employeeName = `${employee.first_name} ${employee.last_name}`.toLowerCase().trim()
    const userName = `${employee.user_account.first_name} ${employee.user_account.last_name}`.toLowerCase().trim()
    
    return {
      matches: employeeName === userName,
      employee_name: `${employee.first_name} ${employee.last_name}`,
      user_name: `${employee.user_account.first_name} ${employee.user_account.last_name}`,
      suggestion: employeeName !== userName ? 'Consider updating names to match for consistency' : 'Names match perfectly'
    }
  },

  // Get employees with user account analysis
  getEmployeesWithUserAnalysis: async () => {
    try {
      const response = await api.get('/employees')
      const employees = response.data || []
      
      return {
        success: true,
        data: employees.map(employee => ({
          ...employee,
          user_analysis: {
            has_user_account: !!employee.user_account,
            can_login: !!employee.user_account?.is_active,
            name_consistency: employeeService.checkNameConsistency(employee)
          }
        }))
      }
    } catch (error) {
      console.error('Error fetching employees with analysis:', error)
      return {
        success: false,
        message: error.message || 'Failed to fetch employees',
        data: []
      }
    }
  },

  // Link employee to user
  linkToUser: async (employeeId, userId) => {
    try {
      const response = await api.post(`/employees/${employeeId}/link-user`, { user_id: userId })
      return {
        success: true,
        data: response.data,
        message: 'Employee linked to user successfully'
      }
    } catch (error) {
      console.error('Error linking employee to user:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to link employee to user'
      }
    }
  },

  // Unlink employee from user
  unlinkFromUser: async (employeeId) => {
    try {
      const response = await api.delete(`/employees/${employeeId}/link-user`)
      return {
        success: true,
        data: response.data,
        message: 'Employee unlinked from user successfully'
      }
    } catch (error) {
      console.error('Error unlinking employee from user:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to unlink employee from user'
      }
    }
  }
}

export default employeeService